import AxiosInstance, { setSid } from 'services/apiAdapter';
import { handleLogout } from 'services/authService';
import { fetchAllEnums } from 'services/enumService';
import { getBanks } from 'services/paymentsService';
import { getCountries } from 'services/userService';
import { EnumsInterface } from 'types/EnumsInterface';
import { BankInterface } from 'types/PaymentsInterface';
import { async_status, RESPONSE_SUCCESS } from 'types/ResponseTypes';
import { CountryInterface } from 'types/UserInterface';
import { create } from 'zustand';

export interface GetUserResponseInterface {
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    middleName: string;
    lastLoginDate: string;
    mobile: string;
    email: string;
    sid: string;
    createdAt: string;
    gender: string;
    dateOfBirth: string;
    nationality: string;
    city?: string;
    state?: string;
    address?: string;
    registrationStatus: "PERSONAL_INFORMATION_UPDATED" | "ADDRESS_SUBMITTED" | "ACCOUNT_CREATED" | "EMAIL_VERIFIED" | "MOBILE_VERIFIED" | "SECURITY_QUESTION_SET" | "KYC_VERIFIED" | "COMPLETED";
    country: {
        id: string,
        status: string,
        createdAt: string,
        createdBy: string,
        updatedAt: string,
        updatedBy: string,
        iso: string,
        name: string,
        niceName: string,
        iso3: string,
        numCode: string,
        phoneCode: string,
        currency: string,
    };
    media: {
        id: string;
        fileName: string;
        directory: string;
        contentType: string;
        downloadAccess: string;
        fileSize: string;
        userFileName: string;
    }
}

interface AppState {
    user: GetUserResponseInterface | null;
    userStatus: string;
    fetchUser: (fetchType?: "SILENT") => Promise<void>;
    setUser: (user: GetUserResponseInterface | null) => void;

    sendMoney: boolean;
    setSendMoney: (sendMoney: boolean) => void;

    refreshTokenModalOpen: boolean;
    setRefreshTokenModalOpen: (refreshTokenModalOpen: boolean) => void;

    countries: CountryInterface[] | null;
    countriesStatus: string;
    fetchCountries: () => Promise<void>;
    setCountries: (countries: CountryInterface[] | null) => void;

    banks: BankInterface[] | null;
    banksStatus: string;
    fetchBanks: () => Promise<void>;
    setBanks: (countries: BankInterface[] | null) => void;

    enums: EnumsInterface;
    enumsStatus: string;
    fetchEnums: () => Promise<void>;
    setEnums: (enums: EnumsInterface) => void;
}

const useStore = create<AppState>((set) => ({
    sendMoney: false,
    setSendMoney: (sendMoney) => {
        set({ sendMoney })
    },

    refreshTokenModalOpen: false,
    setRefreshTokenModalOpen: (refreshTokenModalOpen) => set({ refreshTokenModalOpen }),

    user: null,
    userStatus: async_status.__DEFAULT__,
    fetchUser: async (fetchType) => {
        try {
            if (fetchType !== "SILENT") {
                set({ userStatus: async_status.__LOADING__ });
            }

            const { data } = await AxiosInstance.get(`/users/find-logged-in-user`); // Replace with your actual endpoint
            if (data) {
                setSid(data.sid)
                set({ user: data, userStatus: async_status.__LOADED__ });
            } else {
                set({
                    userStatus: async_status.__FAILED__,
                    user: {} as GetUserResponseInterface
                });
            }
        } catch (error) {
            handleLogout()
            set({
                userStatus: async_status.__FAILED__,
                user: {} as GetUserResponseInterface
            });
        }
    },
    setUser: (user) => {
        set({ user });
    },

    countries: null,
    countriesStatus: async_status.__DEFAULT__,
    fetchCountries: async () => {
        try {
            set({ countriesStatus: async_status.__LOADING__ });

            const { data } = await getCountries()
            if (data.message === RESPONSE_SUCCESS) {
                set({ countries: data.data, countriesStatus: async_status.__LOADED__ });
            } else {
                set({
                    countriesStatus: async_status.__FAILED__,
                });
            }
        } catch (error) {
            set({
                countriesStatus: async_status.__FAILED__,
            });
        }
    },
    setCountries: (countries) => {
        set({ countries });
    },



    banks: null,
    banksStatus: async_status.__DEFAULT__,
    fetchBanks: async () => {
        try {
            set({ banksStatus: async_status.__LOADING__ });

            const { data } = await getBanks()
            if (data.message === RESPONSE_SUCCESS) {
                set({ banks: data.data, banksStatus: async_status.__LOADED__ });
            } else {
                set({
                    banksStatus: async_status.__FAILED__,
                });
            }
        } catch (error) {
            set({
                banksStatus: async_status.__FAILED__,
            });
        }
    },
    setBanks: (banks) => {
        set({ banks });
    },




    enums: {} as EnumsInterface,
    enumsStatus: async_status.__DEFAULT__,
    fetchEnums: async () => {
        try {
            set({ enumsStatus: async_status.__LOADING__ });

            const { data } = await fetchAllEnums()
            if (data) {
                set({ enums: data, enumsStatus: async_status.__LOADED__ });
            } else {
                set({
                    enumsStatus: async_status.__FAILED__,
                    enums: {} as EnumsInterface
                });
            }
        } catch (error) {
            set({
                enumsStatus: async_status.__FAILED__,
                enums: {} as EnumsInterface
            });
        }
    },
    setEnums: (enums) => set({ enums }),
}));

export default useStore;
